import { CORPUS, corpusEntry } from '../../data/chorales/corpusIndex';
import { LETTER_KIND } from '../../data/forms';
import { SubTabs } from '../components/Shell';
import { ReturnsMini } from './Returns';
import { Strip } from './Forms';

/** The three ways the atlas compares music with itself. */
export function CompareTabs({ current }: { current: 'tunes' | 'variations' | 'returns' }) {
  const hasSonatas = CORPUS.forms.length > 0 || CORPUS.returns.some((r) => corpusEntry(r.id)?.kind === 'sonata');
  const hasSets = CORPUS.returns.some((r) => corpusEntry(r.id)?.kind === 'variations');
  return (
    <SubTabs
      current={current}
      items={[
        { id: 'tunes', href: '#tunes', label: 'Same tune, new harmony' },
        { id: 'variations', href: '#variations', label: 'Theme and variations', hidden: !hasSets },
        { id: 'returns', href: '#forms', label: 'When the music returns', hidden: !hasSonatas },
      ]}
    />
  );
}

/**
 * The Compare section: hear the same music twice and notice what changes. Each way in is a card
 * with a small picture of what the room shows.
 */
export function CompareHub() {
  const k265 = CORPUS.returns.find((r) => r.id === 'k265');
  const k545 = CORPUS.forms.find((f) => f.id === 'k545-1') ?? CORPUS.forms[0];
  const hasSonatas = CORPUS.forms.length > 0;
  return (
    <div>
      <header className="page-head">
        <span className="label">Compare</span>
        <h1>Hear the same music twice</h1>
        <p>Every room here plays one passage next to another version of itself. Listen for what stays and what changes.</p>
      </header>
      <div className="ways">
        <div className="way">
          <span className="label">Same tune, new harmony</span>
          <h2>One melody, five harmonies</h2>
          <p>Bach sets the Passion Chorale five times in the St Matthew Passion. Hear one melody note mean something different each time.</p>
          <a className="way-go" href="#passion">
            Open the Passion Chorale →
          </a>
          <span className="way-more">
            or <a href="#tunes">browse all {CORPUS.tunes.length} tunes Bach set more than once</a>
          </span>
        </div>
        {k265 && (
          <div className="way">
            <span className="label">Theme and variations</span>
            <h2>The same harmony, new clothes</h2>
            <p>A tune played again and again, each time dressed differently. Start with “Twinkle, Twinkle” (Mozart, K. 265).</p>
            <span className="way-pic">
              <ReturnsMini rows={k265.rows} />
            </span>
            <a className="way-go" href="#variations">
              See {CORPUS.returns.filter((r) => corpusEntry(r.id)?.kind === 'variations').length} sets by Mozart and Beethoven →
            </a>
          </div>
        )}
        {hasSonatas && k545 && (
          <div className="way">
            <span className="label">When the music returns</span>
            <h2>The same music, coming home</h2>
            <p>A sonata states its themes, travels, then plays them again. Hear what Mozart changes so the return can stay at home.</p>
            <span className="way-pic">
              <Strip kinds={[...k545.strip].map((ch) => LETTER_KIND[ch])} width={260} height={26} />
            </span>
            <a className="way-go" href="#forms">
              Compare {CORPUS.forms.length} sonata movements →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
